import { rememberTenant, preferredTenant } from "./tenant-continuity.js";
import { FormEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import QRCode from "qrcode";
import "./styles.css";
import { AccessHeader, AccessStory } from "./access-experience.js";
import { GoogleMark } from "./google-mark.js";
import { MarketingApp } from "./marketing.js";
import { AdminApp } from "./admin.js";
import { CrmWorkspace } from "./crm-workspace.js";
import { BookingPage } from "./booking.js";
import { CalendarWorkspace } from "./calendar-workspace.js";
import { PasswordRecovery } from "./password-recovery.js";
import { PasswordField } from "./password-field.js";
import { PlansWorkspace } from "./plans-workspace.js";
import {
  CommercialBrief,
  type CommercialInsight,
} from "./commercial-insight.js";
import { ChannelOperationalStatus, OperationalBell } from "./operational-indicators.js";
import "./marketing.css";
import "./marketing-home-v2.css";
import "./access-experience.css";

type Tenant = { id: string; name: string; role: string };
type Conversation = {
  id: string;
  external_id: string;
  display_name: string | null;
  ai_mode: "ai" | "human" | "paused";
  operator_name: string | null;
  updated_at: string;
  last_text: string | null;
  last_at: string | null;
  avatar_url: string | null;
  has_unread: boolean;
};
type Message = {
  id: string;
  content_type: string;
  actor: "customer" | "ai" | "human" | "system";
  text: string;
  delivery_state: string;
  delivery_reason: string | null;
  created_at: string;
  media_id: string | null;
  media_url: string | null;
  media_url_expires_at: string | null;
  media_duration_seconds: number | null;
  media_mime_type: string | null;
};
type Profile = {
  companyName: string;
  businessArea: string;
  services: string[];
  commercialInfo: string;
  businessHours: string;
  attendanceRules: string;
  aiInstructions: string;
  dailyBudgetMicros: number;
  recentHistoryLimit: number;
  memoryThreshold: number;
};
type PilotForm = Profile & { allowedContacts: string };
type Subscription = {
  planId: string;
  offerKey: string | null;
  planName: string;
  status:
    | "pending_activation"
    | "trialing"
    | "active"
    | "expired"
    | "suspended"
    | "cancelled";
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  checkoutState: "none" | "preparing" | "scheduled" | "confirmed" | "cancelled";
  checkoutRequestedAt: string | null;
  paymentConfirmedAt: string | null;
  paidActivationAt: string | null;
  cancellationRequestedAt: string | null;
  financial: {
    status: string;
    paymentMethod: "boleto" | "credit_card" | "pix";
    dueAt: string | null;
    amountCents: number;
    lateFeeCents: number;
    graceEndsAt: string | null;
  } | null;
  automaticRepliesAllowed: boolean;
  pendingPaidPlan: { planId: string; planName: string; planVersion: number } | null;
  usage: {
    used: number;
    limit: number;
    remaining: number;
    periodKind: "trial" | "paid";
  } | null;
};
type Channel = {
  status: string;
  pairing_requested: boolean;
  pairing_qr: string | null;
  pairing_qr_expires_at: string | null;
  last_error: string | null;
  allowed_contacts: string[];
  updated_at: string;
};
type CalendarStatus = {
  oauthConfigured: boolean;
  status: string;
  accountEmail: string | null;
  calendarId: string | null;
  lastErrorCode: string | null;
  module?: {
    agencyEnabled: boolean;
    ownerEnabled: boolean;
    operational: boolean;
  };
  settings: {
    enabled: boolean;
    timezone: string;
    businessHours: Array<{ weekday: number; start: string; end: string }>;
    slotIntervalMinutes: number;
    minimumNoticeMinutes: number;
    bufferMinutes: number;
  } | null;
  services: Array<{
    id: string;
    name: string;
    duration_minutes: number;
    active: boolean;
  }>;
};
type GoogleCalendar = {
  id: string;
  name: string;
  primary: boolean;
  timezone: string | null;
};
type CalendarAppointment = {
  id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  service_name: string | null;
  cancelled_at: string | null;
  rescheduled_at: string | null;
};

const blankForm = (): PilotForm => ({
  companyName: "",
  businessArea: "",
  services: [],
  commercialInfo: "",
  businessHours: "",
  attendanceRules: "Atenda em português do Brasil de forma objetiva e cordial.",
  aiInstructions: "",
  dailyBudgetMicros: 50000,
  recentHistoryLimit: 12,
  memoryThreshold: 12,
  allowedContacts: "",
});
const toProfile = ({
  allowedContacts: _allowedContacts,
  ...profile
}: PilotForm): Profile => profile;
const normalizeContacts = (value: string): string[] => {
  const contacts = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return [
    ...new Set(
      contacts.map((contact) => {
        const digits = contact.replace(/\D/g, "");
        const canonical = digits.startsWith("55") ? digits : `55${digits}`;
        if (!/^55[1-9]\d(?:9\d{8}|[2-8]\d{7})$/.test(canonical))
          throw new Error(
            "Informe um telefone brasileiro válido com DDD, por exemplo (11) 99999-9999.",
          );
        return canonical;
      }),
    ),
  ];
};
const formatContact = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const national = digits.startsWith("55") ? digits.slice(2) : digits;
  if (!/^\d{10,11}$/.test(national)) return value;
  const number = national.slice(2);
  return number.length === 9
    ? `(${national.slice(0, 2)}) ${number.slice(0, 5)}-${number.slice(5)}`
    : `(${national.slice(0, 2)}) ${number.slice(0, 4)}-${number.slice(4)}`;
};
export const api = async <T,>(
  path: string,
  token?: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });
  if (!response.ok)
    throw new Error(
      ((await response.json().catch(() => ({}))) as { message?: string })
        .message ?? "Não foi possível concluir a ação.",
    );
  return response.json() as Promise<T>;
};
const formatTime = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value))
    : "";
const initials = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
const displayName = (conversation: Conversation) =>
  conversation.display_name || conversation.external_id;
const modeLabel = (mode: Conversation["ai_mode"]) =>
  mode === "human" ? "Humano" : mode === "paused" ? "Pausada" : "IA ativa";
const deliveryLabel = (message: Message) =>
  message.delivery_state === "failed"
    ? message.delivery_reason === "human_handoff"
      ? "Resposta interrompida ao assumir"
      : message.delivery_reason === "media_payload_invalid"
        ? "Áudio inválido ou incompleto. Grave novamente."
        : message.delivery_reason === "media_conversion_failed"
          ? "Não foi possível preparar o áudio. Tente gravar novamente."
          : "Falha no envio"
    : ((
        {
          read: "Lida",
          delivered: "Entregue",
          accepted: "Enviada",
          sending: "Enviando",
          pending: "Aguardando envio",
          uncertain: "Entrega não confirmada",
        } as Record<string, string>
      )[message.delivery_state] ?? "Estado indisponível");
const deliveryGlyph = (state: string) =>
  state === "read" || state === "delivered" ? "✓✓" : state === "accepted" ? "✓" : state === "pending" || state === "sending" ? "◷" : "!";
function DeliveryIndicator({ message }: { message: Message }) {
  const label = deliveryLabel(message);
  return <small className={`delivery ${message.delivery_state}`} title={label} aria-label={label}><span aria-hidden="true">{deliveryGlyph(message.delivery_state)}</span>{label}</small>;
}
function Avatar({
  name,
  url,
  large = false,
}: {
  name: string;
  url?: string | null;
  large?: boolean;
}) {
  return (
    <span
      className={`avatar ${large ? "large" : ""} ${url ? "has-photo" : ""}`}
    >
      {url ? <img src={url} alt="" /> : initials(name)}
    </span>
  );
}
const durationLabel = (seconds: number) =>
  `${Math.floor(Math.max(0, seconds) / 60)}:${String(Math.floor(Math.max(0, seconds) % 60)).padStart(2, "0")}`;
function AudioPlayer({
  message,
  preview = false,
}: {
  message: Pick<
    Message,
    "actor" | "media_url" | "media_duration_seconds" | "text"
  >;
  preview?: boolean;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(message.media_duration_seconds ?? 0);
  const [failed, setFailed] = useState(false);
  const [peaks, setPeaks] = useState<number[]>([]);
  useEffect(() => {
    let active = true;
    const context = new AudioContext();
    void fetch(message.media_url!)
      .then((response) => response.arrayBuffer())
      .then((buffer) => context.decodeAudioData(buffer))
      .then((decoded) => {
        if (!active) return;
        const samples = decoded.getChannelData(0);
        const width = 20;
        const step = Math.max(1, Math.floor(samples.length / width));
        const next = Array.from({ length: width }, (_, index) => {
          let max = 0;
          for (
            let sample = index * step;
            sample < Math.min(samples.length, (index + 1) * step);
            sample++
          )
            max = Math.max(max, Math.abs(samples[sample]!));
          return Math.max(12, Math.round(max * 100));
        });
        setPeaks(next);
      })
      .catch(() => {
        if (active) setPeaks([]);
      })
      .finally(() => void context.close());
    return () => {
      active = false;
      void context.close();
    };
  }, [message.media_url]);
  const label = preview
    ? "Prévia da gravação"
    : message.actor === "customer"
      ? "Áudio do cliente"
      : message.actor === "ai"
        ? "Áudio da G3.AI"
        : "Áudio do operador";
  if (!message.media_url)
    return (
      <div className="audio-unavailable">
        <strong>{label}</strong>
        <span>Arquivo indisponível ou expirado.</span>
      </div>
    );
  const toggle = () => {
    const element = audio.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => setFailed(true));
    else element.pause();
  };
  const total =
    Number.isFinite(duration) && duration > 0
      ? duration
      : (message.media_duration_seconds ?? 0);
  return (
    <div className="audio-player">
      <div className="audio-player-top">
        <strong>{label}</strong>
        <span>{durationLabel(total)}</span>
      </div>
      <div className="audio-controls">
        <button
          type="button"
          className="audio-toggle"
          onClick={toggle}
          aria-label={playing ? "Pausar áudio" : "Reproduzir áudio"}
        >
          {playing ? "Ⅱ" : "▶"}
        </button>
        <div className="audio-track">
          <input
            aria-label="Progresso do áudio"
            type="range"
            min="0"
            max={Math.max(1, total)}
            step="0.1"
            value={Math.min(position, Math.max(1, total))}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (audio.current) audio.current.currentTime = value;
              setPosition(value);
            }}
          />
          {peaks.length > 0 && (
            <span className="audio-wave" aria-hidden="true">
              {peaks.map((peak, index) => (
                <i key={index} style={{ height: `${peak}%` }} />
              ))}
            </span>
          )}
        </div>
        <span className="audio-time">
          {durationLabel(position)} / {durationLabel(total)}
        </span>
      </div>
      <audio
        ref={audio}
        preload="metadata"
        src={message.media_url}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onError={() => setFailed(true)}
      >
        Seu navegador não suporta reprodução de áudio.
      </audio>
      {failed && (
        <span className="audio-error" role="status">
          Não foi possível reproduzir este áudio. Tente recarregar a conversa.
        </span>
      )}
      {message.text && !preview && (
        <p className="audio-transcript">{message.text}</p>
      )}
    </div>
  );
}

function subscriptionSummary(subscription: Subscription): string {
  const date = (value: string | null) =>
    value
      ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(
          new Date(value),
        )
      : "data indisponível";
  if (subscription.financial?.status === "overdue") {
    const fee = subscription.financial.lateFeeCents > 0
      ? ` Inclui multa única de ${(subscription.financial.lateFeeCents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}.`
      : "";
    return "Cobrança vencida. Regularize até " + date(subscription.financial.graceEndsAt) + "." + fee;
  }
  if (subscription.financial?.status === "scheduled")
    return "Cobrança criada no Asaas Sandbox e aguardando confirmação financeira.";
  if (subscription.status === "pending_activation")
    return "Aguardando a primeira conexão do WhatsApp para iniciar o teste.";
  if (
    subscription.checkoutState === "confirmed" &&
    subscription.status === "trialing"
  )
    return (
      "Assinatura confirmada. O teste segue até " +
      date(subscription.trialEndsAt) +
      "."
    );
  if (
    subscription.checkoutState === "scheduled" ||
    subscription.checkoutState === "preparing"
  )
    return (
      "Assinatura em preparação no Sandbox. O teste segue até " +
      date(subscription.trialEndsAt) +
      "."
    );
  if (subscription.status === "trialing") {
    const days = subscription.trialEndsAt
      ? Math.max(
          0,
          Math.ceil(
            (new Date(subscription.trialEndsAt).getTime() - Date.now()) /
              86400000,
          ),
        )
      : null;
    return (
      (days !== null && days <= 2
        ? days === 0
          ? "Seu teste termina hoje."
          : "Falta " +
            days +
            (days === 1 ? " dia" : " dias") +
            " para o fim do teste."
        : "Teste gratuito até " + date(subscription.trialEndsAt)) + " "
    );
  }
  if (subscription.status === "active")
    return (
      "Assinatura ativa até " + date(subscription.currentPeriodEndsAt) + "."
    );
  if (subscription.status === "expired")
    return "Teste encerrado: respostas automáticas pausadas. O Inbox e o histórico permanecem disponíveis.";
  if (subscription.status === "suspended")
    return "Acesso suspenso: respostas automáticas pausadas.";
  return "Assinatura cancelada: respostas automáticas pausadas.";
}
function subscriptionLabel(subscription: Subscription): string {
  return subscription.status === "pending_activation"
    ? "Aguardando"
    : subscription.status === "trialing"
      ? "Teste"
      : subscription.status === "active"
        ? "Ativo"
        : subscription.status === "expired"
          ? "Vencido"
          : subscription.status === "suspended"
            ? "Suspenso"
            : "Cancelado";
}
type AppIconName =
  | "overview"
  | "inbox"
  | "pipeline"
  | "calendar"
  | "settings"
  | "logout"
  | "company"
  | "clock"
  | "brain"
  | "shield";
function AppIcon({ name }: { name: AppIconName }) {
  const paths: Record<AppIconName, React.ReactNode> = {
    overview: (
      <>
        <rect x="4" y="4" width="6" height="6" rx="1" />
        <rect x="14" y="4" width="6" height="6" rx="1" />
        <rect x="4" y="14" width="6" height="6" rx="1" />
        <rect x="14" y="14" width="6" height="6" rx="1" />
      </>
    ),
    inbox: (
      <>
        <path d="M20 15a4 4 0 0 1-4 4H8l-4 3V7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8Z" />
        <path d="M8 10h8M8 14h5" />
      </>
    ),
    pipeline: (
      <>
        <circle cx="6" cy="6" r="2" />
        <circle cx="18" cy="6" r="2" />
        <circle cx="12" cy="18" r="2" />
        <path d="m7.6 7.2 3.1 8.1M16.4 7.2l-3.1 8.1" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.12 2.12-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.04 1.56V20.3h-3v-.08A1.7 1.7 0 0 0 10.66 18.66a1.7 1.7 0 0 0-1.88.34l-.06.06-2.12-2.12.06-.06A1.7 1.7 0 0 0 7 15a1.7 1.7 0 0 0-1.56-1.04h-.08v-3h.08A1.7 1.7 0 0 0 7 9.92a1.7 1.7 0 0 0-.34-1.88l-.06-.06L8.72 5.86l.06.06A1.7 1.7 0 0 0 10.66 6.26 1.7 1.7 0 0 0 11.7 4.7v-.08h3v.08a1.7 1.7 0 0 0 1.04 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.12 2.12-.06.06A1.7 1.7 0 0 0 19.4 9.92a1.7 1.7 0 0 0 1.56 1.04h.08v3h-.08A1.7 1.7 0 0 0 19.4 15Z" />
      </>
    ),
    logout: (
      <>
        <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
        <path d="m14 16 4-4-4-4M18 12H9" />
      </>
    ),
    company: (
      <>
        <path d="M4 21V5l8-2v18M4 10h8M8 7h.01M8 13h.01M8 17h.01M15 9h5v12h-5" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    brain: (
      <>
        <path d="M9 4a3 3 0 0 0-5 2 3 3 0 0 0 .6 5.7A3.4 3.4 0 0 0 8 17h1M15 4a3 3 0 0 1 5 2 3 3 0 0 1-.6 5.7A3.4 3.4 0 0 1 16 17h-1M9 4a3 3 0 0 1 3 3v13M15 4a3 3 0 0 0-3 3" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 20 6v5c0 5-3.3 8.4-8 10-4.7-1.6-8-5-8-10V6l8-3Z" />
        <path d="m8.5 12 2.2 2.2 4.8-4.8" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
function App() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem("g3ai-inbox-token") ?? "",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [view, setView] = useState<
    "dashboard" | "pipeline" | "inbox" | "settings" | "preparation" | "calendar" | "plans"
  >(() => {
    if (window.location.pathname.replace(/\/+$/, "") === "/qa/preparation-360-v2")
      return "preparation";
    const requested = new URLSearchParams(window.location.search).get("view");
    return requested === "plans" || requested === "inbox" ||
      requested === "pipeline" ||
      requested === "settings" ||
      requested === "preparation" ||
      requested === "calendar"
      ? requested
      : "dashboard";
  });
  const [crmFocusId, setCrmFocusId] = useState("");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [profile, setProfile] = useState<Profile | null>(null);
  const [channel, setChannel] = useState<Channel | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [calendar, setCalendar] = useState<CalendarStatus | null>(null);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  const [calendarAppointments, setCalendarAppointments] = useState<
    CalendarAppointment[]
  >([]);
  const [calendarModal, setCalendarModal] = useState(false);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [commercialInsight, setCommercialInsight] =
    useState<CommercialInsight | null>(null);
  const [commercialInsightLoading, setCommercialInsightLoading] =
    useState(false);
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [modeFilter, setModeFilter] = useState<"all" | "ai" | "human">("all");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [recording, setRecording] = useState<
    "idle" | "recording" | "paused" | "ready"
  >("idle");
  const [recorded, setRecorded] = useState<{
    blob: Blob;
    url: string;
    durationSeconds: number;
  } | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [liveWave, setLiveWave] = useState<number[]>([]);
  const recorder = useRef<MediaRecorder | null>(null);
  const recordingChunks = useRef<Blob[]>([]);
  const recordingStartedAt = useRef(0);
  const recordingElapsedMs = useRef(0);
  const recordingStream = useRef<MediaStream | null>(null);
  const analyserContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const analyserFrame = useRef<number | null>(null);
  const discardPending = useRef(false);
  const recordingIsActive = useRef(false);
  const [formMode, setFormMode] = useState<"edit" | null>(null);
  const [form, setForm] = useState<PilotForm>(blankForm);
  const [formError, setFormError] = useState("");
  const [qrImage, setQrImage] = useState("");
  const messagesPane = useRef<HTMLDivElement>(null);
  const [commercialBriefExpanded, setCommercialBriefExpanded] = useState(true);
  const briefScrollAnchor = useRef<{ top: number; follow: boolean } | null>(null);
  // The Inbox follows the latest message only while the operator is already
  // reading the end of the thread. This avoids pulling someone away from a
  // historical message whenever the polling refresh returns.
  const followLatestMessage = useRef(true);
  const unreadConversationIds = useRef(new Set<string>());
  const readStateBootstrapKey = useRef("");
  const selected = conversations.find(
    (conversation) => conversation.id === selectedId,
  );
  const tenant = tenants.find((item) => item.id === tenantId);
  const visible = useMemo(
    () =>
      conversations.filter(
        (conversation) =>
          (modeFilter === "all" || conversation.ai_mode === modeFilter) &&
          `${displayName(conversation)} ${conversation.last_text ?? ""}`
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()),
      ),
    [conversations, query, modeFilter],
  );
  const unreadConversationCount = useMemo(
    () => conversations.filter((conversation) => conversation.has_unread).length,
    [conversations],
  );
  const updateForm = (changes: Partial<PilotForm>) => {
    setFormError("");
    setForm((current) => ({ ...current, ...changes }));
  };

  useEffect(() => {
    let live = true;
    void api<{ data: { google: boolean } }>("/v1/auth/options")
      .then(({ data }) => live && setGoogleEnabled(data.google))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (token) return;
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const code = hash.get("google_code");
    const error = hash.get("google_error");
    if (error) {
      setLoginError(
        error === "google_cancelled"
          ? "A entrada com Google foi cancelada."
          : "Não foi possível concluir a entrada com Google. Tente novamente.",
      );
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${window.location.search}`,
      );
      return;
    }
    if (!code) return;
    setLoginBusy(true);
    setLoginError("");
    void api<{ token: string; returnTo: string }>(
      "/v1/auth/google/sign-in/exchange",
      undefined,
      { method: "POST", body: JSON.stringify({ code }) },
    )
      .then((response) => {
        sessionStorage.setItem("g3ai-inbox-token", response.token);
        window.history.replaceState({}, "", response.returnTo);
        setToken(response.token);
      })
      .catch((cause: Error) => {
        setLoginError(cause.message);
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${window.location.search}`,
        );
      })
      .finally(() => setLoginBusy(false));
  }, [token]);
  useEffect(() => {
    if (!token) return;
    const requestedTenant = new URLSearchParams(window.location.search).get("tenant");
    void api<{ data: Tenant[] }>("/v1/tenants", token)
      .then(({ data }) => {
        setTenants(data);
        setTenantId((current) =>
          data.some((item) => item.id === current)
            ? current
            : (preferredTenant(data, requestedTenant)?.id ?? ""),
        );
      })
      .catch(() => {
        setTenants([]);
        setTenantId("");
        setToken("");
        sessionStorage.removeItem("g3ai-inbox-token");
        setLoginError("Sua sessão expirou. Entre novamente.");
      });
  }, [token]);
  useEffect(() => {
    if (!token || !tenantId) return;
    rememberTenant(tenantId);
    setProfile(null); setChannel(null); setSubscription(null); setCalendar(null); setQrImage("");
    let active = true;
    const refresh = () =>
      Promise.all([
        api<{ data: { config: Profile } }>(
          `/v1/tenants/${tenantId}/profile`,
          token,
        ).catch((cause:Error)=>{if(cause.message==="Tenant configuration not found") return null; throw cause;}),
        api<{ data: Channel }>(`/v1/tenants/${tenantId}/channel`, token).catch((cause:Error)=>{if(cause.message==="WhatsApp channel not found") return null; throw cause;}),
        api<{ data: Subscription }>(
          `/v1/tenants/${tenantId}/subscription`,
          token,
        ),
        api<{ data: CalendarStatus }>(
          `/v1/tenants/${tenantId}/calendar`,
          token,
        ),
      ])
        .then(
          ([
            profileResponse,
            channelResponse,
            subscriptionResponse,
            calendarResponse,
          ]) => {
            if (!active) return;
            setProfile(profileResponse?.data.config ?? null);
            setChannel(channelResponse?.data ?? null);
            setSubscription(subscriptionResponse.data);
            setCalendar(calendarResponse.data);
            if (channelResponse?.data.pairing_qr)
              void QRCode.toDataURL(channelResponse.data.pairing_qr, {
                margin: 1,
                width: 244,
                errorCorrectionLevel: "M",
              }).then((value) => active && setQrImage(value));
            else setQrImage("");
          },
        )
        .catch(async (cause: Error) => {
          if (["WhatsApp channel not found", "Commercial subscription not found"].includes(cause.message) && window.location.pathname.replace(/\/+$/, "") === "/qa/preparation-360-v2") {
            try {
              const status = await api<{data:{qa?:{enabled?:boolean}}}>(`/v1/tenants/${tenantId}/preparation/copilot/status`, token);
              if (status.data.qa?.enabled) return;
            } catch { /* Preserve the original error if QA cannot be verified. */ }
          }
          if (active) setError(cause.message);
        });
    void refresh();
    const interval = window.setInterval(refresh, 2500);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [token, tenantId]);
  useEffect(() => {
    if (!token || !tenantId) return;
    let active = true;
    const refresh = () =>
      api<{ data: Conversation[] }>(
        `/v1/tenants/${tenantId}/conversations`,
        token,
      )
        .then(({ data }) => {
          if (active) {
            unreadConversationIds.current = new Set(
              data.filter((item) => item.has_unread).map((item) => item.id),
            );
            setConversations(data);
            setSelectedId((current) =>
              data.some((item) => item.id === current)
                ? current
                : (data[0]?.id ?? ""),
            );
            const bootstrapKey = `${tenantId}:${token.slice(-16)}`;
            if (readStateBootstrapKey.current !== bootstrapKey) {
              readStateBootstrapKey.current = bootstrapKey;
              void api(
                `/v1/tenants/${tenantId}/conversations/read-state/bootstrap`,
                token,
                { method: "POST" },
              ).catch((cause: Error) => active && setError(cause.message));
            }
          }
        })
        .catch((cause: Error) => active && setError(cause.message));
    void refresh();
    const interval = window.setInterval(refresh, 4000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [token, tenantId]);
  useEffect(() => {
    if (!token || !tenantId || !selectedId) return;
    let active = true;
    const refresh = () =>
      api<{ data: Message[] }>(
        `/v1/tenants/${tenantId}/conversations/${selectedId}/messages`,
        token,
      )
        .then(({ data }) => {
          if (!active) return;
          setMessages((previous) => {
            const retained = new Map(
              previous.map((message) => [message.id, message]),
            );
            return data.map((message) => {
              const prior = retained.get(message.id);
              const validPriorUrl =
                prior?.media_id &&
                prior.media_id === message.media_id &&
                prior.media_url &&
                prior.media_url_expires_at &&
                new Date(prior.media_url_expires_at).getTime() - Date.now() >
                  30_000;
              return validPriorUrl
                ? {
                    ...message,
                    media_url: prior.media_url,
                    media_url_expires_at: prior.media_url_expires_at,
                  }
                : message;
            });
          });
        })
        .catch((cause: Error) => active && setError(cause.message));
    void refresh();
    const interval = window.setInterval(refresh, 3000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [token, tenantId, selectedId]);
  const refreshCommercialInsight = () => {
    if (!token || !tenantId || !selectedId) return;
    setCommercialInsightLoading(true);
    void api<{ data: CommercialInsight | null }>(
      `/v1/tenants/${tenantId}/conversations/${selectedId}/commercial-insight`,
      token,
    )
      .then(({ data }) => setCommercialInsight(data))
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setCommercialInsightLoading(false));
  };
  useEffect(() => {
    setCommercialInsight(null);
    if (selectedId) refreshCommercialInsight();
  }, [token, tenantId, selectedId]);
  useLayoutEffect(() => {
    // Opening a conversation is intentional navigation, so it should always
    // begin at the current message. This is intentionally a layout effect so
    // the follow state is ready before the first post-render scroll sync.
    followLatestMessage.current = true;
  }, [selectedId]);
  useLayoutEffect(() => {
    if (!followLatestMessage.current) return;
    let frame = 0;
    const scrollToLatest = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const pane = messagesPane.current;
        if (!pane || !followLatestMessage.current) return;
        // scrollHeight is read only after React committed the new history.
        // This handles switching between two conversations with the same
        // number of messages, which a messages.length dependency misses.
        pane.scrollTop = pane.scrollHeight;
        if (isNearMessageEnd(pane) && unreadConversationIds.current.has(selectedId))
          void markConversationRead(selectedId);
      });
    };
    const pane = messagesPane.current;
    if (!pane) return;
    scrollToLatest();
    const mutations = new MutationObserver(scrollToLatest);
    mutations.observe(pane, { childList: true, subtree: true, characterData: true });
    const resizes = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scrollToLatest);
    resizes?.observe(pane);
    for (const child of Array.from(pane.children)) resizes?.observe(child);
    return () => {
      window.cancelAnimationFrame(frame);
      mutations.disconnect();
      resizes?.disconnect();
    };
  }, [selectedId, messages]);

  useLayoutEffect(() => {
    const anchor = briefScrollAnchor.current;
    const pane = messagesPane.current;
    if (!anchor || !pane) return;
    followLatestMessage.current = anchor.follow;
    pane.scrollTop = anchor.follow ? pane.scrollHeight : anchor.top;
    briefScrollAnchor.current = null;
  }, [commercialBriefExpanded]);

  function isNearMessageEnd(pane: HTMLDivElement) {
    // A small tolerance covers fractional pixels and media layout settling.
    return pane.scrollHeight - pane.scrollTop - pane.clientHeight < 96;
  }

  function toggleCommercialBrief() {
    const pane = messagesPane.current;
    briefScrollAnchor.current = pane
      ? { top: pane.scrollTop, follow: isNearMessageEnd(pane) }
      : null;
    setCommercialBriefExpanded((current) => !current);
  }

  function openConversation(conversationId: string) {
    if (conversationId !== selectedId) {
      // Do not leave the previous contact's history visible while the next
      // request is in flight. The layout observer will move the new history
      // to its latest message as soon as it is committed.
      followLatestMessage.current = true;
      setMessages([]);
    }
    setSelectedId(conversationId);
  }

  function trackMessageScroll() {
    const pane = messagesPane.current;
    if (!pane) return;
    followLatestMessage.current = isNearMessageEnd(pane);
    if (followLatestMessage.current && selectedId)
      void markConversationRead(selectedId);
  }

  async function markConversationRead(conversationId: string) {
    if (!token || !tenantId || !unreadConversationIds.current.has(conversationId)) return;
    unreadConversationIds.current.delete(conversationId);
    setConversations((current) => current.map((item) => item.id === conversationId ? { ...item, has_unread: false } : item));
    try {
      await api(`/v1/tenants/${tenantId}/conversations/${conversationId}/read`, token, { method: "POST" });
    } catch (cause) {
      unreadConversationIds.current.add(conversationId);
      setConversations((current) => current.map((item) => item.id === conversationId ? { ...item, has_unread: true } : item));
      setError((cause as Error).message);
    }
  }

  async function login(event: FormEvent) {
    event.preventDefault();
    if (loginBusy) return;
    setLoginBusy(true);
    setLoginError("");
    try {
      const response = await api<{ token: string }>(
        "/v1/auth/login",
        undefined,
        { method: "POST", body: JSON.stringify({ email, password }) },
      );
      sessionStorage.setItem("g3ai-inbox-token", response.token);
      setToken(response.token);
      setPassword("");
    } catch (cause) {
      setLoginError((cause as Error).message);
    } finally {
      setLoginBusy(false);
    }
  }
  async function signInWithGoogle() {
    if (loginBusy) return;
    setLoginBusy(true);
    setLoginError("");
    try {
      const response = await api<{ data: { authorizationUrl: string } }>(
        "/v1/auth/google/sign-in/start",
        undefined,
        {
          method: "POST",
          body: JSON.stringify({
            returnTo: `${window.location.pathname}${window.location.search}`,
          }),
        },
      );
      window.location.assign(response.data.authorizationUrl);
    } catch (cause) {
      setLoginError((cause as Error).message);
      setLoginBusy(false);
    }
  }
  function logout() {
    sessionStorage.removeItem("g3ai-inbox-token");
    setToken("");
    setTenants([]);
    setTenantId("");
    setProfile(null);
    setChannel(null);
    setSubscription(null);
    setCalendar(null);
    setConversations([]);
    setSelectedId("");
    setMessages([]);
    setCommercialInsight(null);
    setLoginError("");
  }
  function openEdit() {
    if (!profile) return;
    setError("");
    setFormError("");
    setForm({
      ...profile,
      allowedContacts:
        channel?.allowed_contacts.map(formatContact).join(", ") ?? "",
    });
    setFormMode("edit");
  }
  async function saveCompany(event: FormEvent) {
    event.preventDefault();
    if (!tenantId) return;
    setError("");
    setFormError("");
    let allowedContacts: string[];
    try {
      allowedContacts = normalizeContacts(form.allowedContacts);
    } catch (cause) {
      setFormError((cause as Error).message);
      return;
    }
    setBusy(true);
    try {
      await api(`/v1/tenants/${tenantId}/profile`, token, {
        method: "PUT",
        body: JSON.stringify(toProfile(form)),
      });
      const channelResult = await api<{ data: { allowedContacts: string[] } }>(
        `/v1/tenants/${tenantId}/channel`,
        token,
        { method: "PUT", body: JSON.stringify({ allowedContacts }) },
      );
      setProfile(toProfile(form));
      setChannel((current) =>
        current
          ? { ...current, allowed_contacts: channelResult.data.allowedContacts }
          : current,
      );
      setFormMode(null);
    } catch (cause) {
      setFormError(
        (cause as Error).message ||
          "Não foi possível salvar. Revise os campos e tente novamente.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function beginPairing() {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/tenants/${tenantId}/channel/pairing`, token, {
        method: "POST",
      });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function openCalendar() {
    if (!tenantId) return;
    setError("");
    setCalendarModal(true);
    try {
      const [appointments, calendars] = await Promise.all([
        api<{ data: CalendarAppointment[] }>(
          `/v1/tenants/${tenantId}/calendar/appointments`,
          token,
        ),
        calendar?.status === "connected"
          ? api<{ data: GoogleCalendar[] }>(
              `/v1/tenants/${tenantId}/calendar/calendars`,
              token,
            )
          : Promise.resolve({ data: [] as GoogleCalendar[] }),
      ]);
      setCalendarAppointments(appointments.data);
      setGoogleCalendars(calendars.data);
    } catch (cause) {
      setError((cause as Error).message);
    }
  }
  async function connectGoogleCalendar() {
    if (!tenantId) return;
    setCalendarSaving(true);
    setError("");
    try {
      const response = await api<{ data: { authorizationUrl: string } }>(
        `/v1/tenants/${tenantId}/calendar/oauth/start`,
        token,
        { method: "POST" },
      );
      window.location.assign(response.data.authorizationUrl);
    } catch (cause) {
      setError((cause as Error).message);
      setCalendarSaving(false);
    }
  }
  async function selectGoogleCalendar(calendarId: string) {
    if (!tenantId || !calendarId) return;
    setCalendarSaving(true);
    try {
      await api(`/v1/tenants/${tenantId}/calendar/calendar`, token, {
        method: "PUT",
        body: JSON.stringify({ calendarId }),
      });
      const response = await api<{ data: CalendarStatus }>(
        `/v1/tenants/${tenantId}/calendar`,
        token,
      );
      setCalendar(response.data);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setCalendarSaving(false);
    }
  }
  async function saveCalendarSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tenantId) return;
    const data = new FormData(event.currentTarget);
    setCalendarSaving(true);
    try {
      const settings = {
        enabled: data.get("enabled") === "on",
        timezone: String(data.get("timezone") || "America/Sao_Paulo"),
        businessHours: [
          {
            weekday: 1,
            start: String(data.get("weekdayStart") || "09:00"),
            end: String(data.get("weekdayEnd") || "18:00"),
          },
          {
            weekday: 2,
            start: String(data.get("weekdayStart") || "09:00"),
            end: String(data.get("weekdayEnd") || "18:00"),
          },
          {
            weekday: 3,
            start: String(data.get("weekdayStart") || "09:00"),
            end: String(data.get("weekdayEnd") || "18:00"),
          },
          {
            weekday: 4,
            start: String(data.get("weekdayStart") || "09:00"),
            end: String(data.get("weekdayEnd") || "18:00"),
          },
          {
            weekday: 5,
            start: String(data.get("weekdayStart") || "09:00"),
            end: String(data.get("weekdayEnd") || "18:00"),
          },
        ],
        slotIntervalMinutes: Number(data.get("slotIntervalMinutes") || 30),
        minimumNoticeMinutes: Number(data.get("minimumNoticeMinutes") || 60),
        bufferMinutes: Number(data.get("bufferMinutes") || 0),
      };
      await api(`/v1/tenants/${tenantId}/calendar/settings`, token, {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      const serviceName = String(data.get("serviceName") || "").trim();
      const existingService = calendar?.services[0];
      if (serviceName)
        await api(
          `/v1/tenants/${tenantId}/calendar/services${existingService ? `/${existingService.id}` : ""}`,
          token,
          {
            method: existingService ? "PUT" : "POST",
            body: JSON.stringify({
              name: serviceName,
              durationMinutes: Number(
                data.get("serviceDurationMinutes") ||
                  existingService?.duration_minutes ||
                  30,
              ),
              active: true,
            }),
          },
        );
      const response = await api<{ data: CalendarStatus }>(
        `/v1/tenants/${tenantId}/calendar`,
        token,
      );
      setCalendar(response.data);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setCalendarSaving(false);
    }
  }
  async function transition(action: "claim" | "resume-ai") {
    if (!selected || !tenantId) return;
    setBusy(true);
    setError("");
    try {
      await api(
        `/v1/tenants/${tenantId}/conversations/${selected.id}/${action}`,
        token,
        { method: "POST" },
      );
      setConversations((items) =>
        items.map((item) =>
          item.id === selected.id
            ? { ...item, ai_mode: action === "claim" ? "human" : "ai" }
            : item,
        ),
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function send(event: FormEvent) {
    event.preventDefault();
    if (!selected || !draft.trim() || !tenantId) return;
    setBusy(true);
    setError("");
    try {
      await api(
        `/v1/tenants/${tenantId}/conversations/${selected.id}/messages`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            text: draft.trim(),
            clientMessageId: crypto.randomUUID(),
          }),
        },
      );
      setDraft("");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function stopLiveAnalysis() {
    if (analyserFrame.current !== null)
      cancelAnimationFrame(analyserFrame.current);
    analyserFrame.current = null;
    analyser.current?.disconnect();
    analyser.current = null;
    const context = analyserContext.current;
    analyserContext.current = null;
    if (context && context.state !== "closed") void context.close();
  }
  function renderLiveWave() {
    const current = analyser.current;
    if (!current) return;
    const bins = new Uint8Array(current.fftSize);
    current.getByteTimeDomainData(bins);
    const columns = 28;
    const step = Math.max(1, Math.floor(bins.length / columns));
    const values = Array.from({ length: columns }, (_, index) => {
      let total = 0;
      for (
        let i = index * step;
        i < Math.min(bins.length, (index + 1) * step);
        i++
      )
        total += Math.abs(bins[i]! - 128);
      return Math.max(6, Math.min(100, Math.round((total / step) * 2.4)));
    });
    setLiveWave(values);
    setRecordingSeconds(
      Math.floor(
        (recordingElapsedMs.current + Date.now() - recordingStartedAt.current) /
          1000,
      ),
    );
    analyserFrame.current = requestAnimationFrame(renderLiveWave);
  }
  function beginLiveAnalysis(stream: MediaStream) {
    stopLiveAnalysis();
    const context = new AudioContext();
    const node = context.createAnalyser();
    node.fftSize = 256;
    node.smoothingTimeConstant = 0.78;
    context.createMediaStreamSource(stream).connect(node);
    analyserContext.current = context;
    analyser.current = node;
    void context.resume();
    analyserFrame.current = requestAnimationFrame(renderLiveWave);
  }
  async function beginRecording() {
    if (
      !selected ||
      selected.ai_mode !== "human" ||
      !navigator.mediaDevices?.getUserMedia ||
      !window.MediaRecorder
    ) {
      setError(
        "A gravação está disponível apenas após assumir a conversa, em um navegador com microfone.",
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const current = new MediaRecorder(stream, { mimeType });
      recordingChunks.current = [];
      recordingElapsedMs.current = 0;
      recordingStartedAt.current = Date.now();
      recordingIsActive.current = true;
      discardPending.current = false;
      recordingStream.current = stream;
      setRecordingSeconds(0);
      setLiveWave([]);
      beginLiveAnalysis(stream);
      current.ondataavailable = (event) => {
        if (event.data.size) recordingChunks.current.push(event.data);
      };
      current.onstop = () => {
        const elapsed =
          recordingElapsedMs.current +
          (recordingIsActive.current
            ? Math.max(0, Date.now() - recordingStartedAt.current)
            : 0);
        recordingIsActive.current = false;
        stream.getTracks().forEach((track) => track.stop());
        recordingStream.current = null;
        stopLiveAnalysis();
        setLiveWave([]);
        const blob = new Blob(recordingChunks.current, {
          type: current.mimeType || "audio/webm",
        });
        if (!discardPending.current && blob.size) {
          if (recorded) URL.revokeObjectURL(recorded.url);
          setRecorded({
            blob,
            url: URL.createObjectURL(blob),
            durationSeconds: Math.max(1, Math.ceil(elapsed / 1000)),
          });
          setRecording("ready");
        } else setRecording("idle");
        recorder.current = null;
      };
      recorder.current = current;
      current.start(250);
      setRecording("recording");
    } catch {
      setError(
        "Não foi possível acessar o microfone. Verifique a permissão do navegador.",
      );
    }
  }
  function pauseRecording() {
    if (recorder.current?.state === "recording") {
      recordingElapsedMs.current += Math.max(
        0,
        Date.now() - recordingStartedAt.current,
      );
      recordingIsActive.current = false;
      setRecordingSeconds(Math.floor(recordingElapsedMs.current / 1000));
      recorder.current.pause();
      stopLiveAnalysis();
      setRecording("paused");
    } else if (
      recorder.current?.state === "paused" &&
      recordingStream.current
    ) {
      recordingStartedAt.current = Date.now();
      recordingIsActive.current = true;
      recorder.current.resume();
      beginLiveAnalysis(recordingStream.current);
      setRecording("recording");
    }
  }
  function stopRecording() {
    if (recorder.current && recorder.current.state !== "inactive")
      recorder.current.stop();
  }
  function discardRecording() {
    discardPending.current = true;
    recordingIsActive.current = false;
    if (recorder.current && recorder.current.state !== "inactive")
      recorder.current.stop();
    else stopLiveAnalysis();
    if (recorded) URL.revokeObjectURL(recorded.url);
    setRecorded(null);
    setLiveWave([]);
    setRecordingSeconds(0);
    setRecording("idle");
  }
  async function sendRecording() {
    if (!selected || !tenantId || !recorded || selected.ai_mode !== "human")
      return;
    setBusy(true);
    setError("");
    try {
      const audioBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(String(reader.result).split(",", 2)[1] ?? "");
        reader.onerror = () =>
          reject(new Error("Não foi possível preparar o áudio."));
        reader.readAsDataURL(recorded.blob);
      });
      await api(
        `/v1/tenants/${tenantId}/conversations/${selected.id}/audio`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            clientMessageId: crypto.randomUUID(),
            mimeType: recorded.blob.type || "audio/webm",
            byteLength: recorded.blob.size,
            durationSeconds: recorded.durationSeconds,
            audioBase64,
          }),
        },
      );
      URL.revokeObjectURL(recorded.url);
      setRecorded(null);
      setRecording("idle");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!token)
    return (
      <div className="access-page access-login-page">
        <AccessHeader login />
        <main className="access-layout">
          <AccessStory login />
          <div className="access-form-column">
            <section className="login-card">
              <div className="brand login-brand">
                <span className="brand-mark">G3</span>
                <span>
                  G3<span className="brand-accent">.AI</span>
                </span>
              </div>
              <p className="kicker">SEU ESPAÇO DE TRABALHO</p>
              <h1>Bom ter você de volta.</h1>
              <p className="login-copy">
                Entre para acompanhar suas conversas e cuidar do que faz sua
                empresa crescer.
              </p>
              <form onSubmit={login} aria-busy={loginBusy}>
                <label>
                  E-mail da sua conta
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoFocus
                    required
                    autoComplete="username"
                    placeholder="voce@suaempresa.com.br"
                  />
                </label>
                <label>
                  Senha
                  <PasswordField
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Sua senha de acesso"
                  />
                </label>
                {loginError && (
                  <p className="form-error" role="alert">
                    {loginError}
                  </p>
                )}
                <button className="primary wide" disabled={loginBusy}>
                  {loginBusy ? "Entrando…" : "Entrar no painel →"}
                </button>
              </form>
              {googleEnabled && (
                <>
                  <div className="login-divider">
                    <span>ou</span>
                  </div>
                  <button
                    type="button"
                    className="google-login"
                    onClick={() => void signInWithGoogle()}
                    disabled={loginBusy}
                  >
                    <GoogleMark /> Entrar com Google
                  </button>
                  <p className="google-note">
                    Use uma conta Google já vinculada ao seu acesso G3.AI.
                  </p>
                </>
              )}
              <p className="secure-note">
                Ainda não tem uma conta?{" "}
                <a href="/comecar">Experimente a G3.AI</a>
              </p>
              <p className="secure-note">
                <a href="/recuperar-acesso">Esqueci minha senha</a>
              </p>
            </section>
            <p className="access-admin-note">
              Ambiente de prévia com acesso autorizado.
            </p>
          </div>
        </main>
      </div>
    );
  return (
    <>
      <main
        className={`app-shell ${mobileThreadOpen ? "mobile-thread-open" : ""} ${view === "inbox" ? "" : "app-shell-workspace"} ${view === "preparation" ? "app-shell-preparation" : ""}`}
      >
        <aside className="sidebar">
          <div className="brand">
            <span className="brand-mark">G3</span>
            <span>
              G3<span className="brand-accent">.AI</span>
            </span>
          </div>
          <div className="workspace-label">PAINEL DA EMPRESA</div>
          <div className="sidebar-tenant">
            <span className="sidebar-tenant-icon">
              <AppIcon name="company" />
            </span>
            <div>
              <small>EMPRESA AUTORIZADA</small>
              <strong>{tenant?.name ?? "Carregando empresa"}</strong>
            </div>
          </div>
          <nav aria-label="Navegação principal">
            <button
              type="button"
              className={`nav-item ${view === "dashboard" ? "active" : ""}`}
              onClick={() => setView("dashboard")}
            >
              <AppIcon name="overview" />
              <span>Visão geral</span>
            </button>
            <button
              type="button"
              className={`nav-item ${view === "inbox" ? "active" : ""}`}
              onClick={() => setView("inbox")}
            >
              <AppIcon name="inbox" />
              <span>Inbox</span>
              {unreadConversationCount > 0 && (
                <b className="nav-unread" aria-label={`${unreadConversationCount} conversa${unreadConversationCount === 1 ? "" : "s"} com mensagem nova`}>
                  {unreadConversationCount}
                </b>
              )}
            </button>
            <button
              type="button"
              className={`nav-item ${view === "pipeline" ? "active" : ""}`}
              onClick={() => setView("pipeline")}
            >
              <AppIcon name="pipeline" />
              <span>Pipeline</span>
              <b>{conversations.length}</b>
            </button>
            <button
              type="button"
              className={`nav-item ${view === "preparation" ? "active" : ""}`}
              onClick={() => setView("preparation")}
            >
              <AppIcon name="brain" />
              <span>Minha IA</span>
            </button>
            <button
              type="button"
              className={`nav-item ${view === "calendar" ? "active" : ""}`}
              onClick={() => setView("calendar")}
            >
              <AppIcon name="calendar" />
              <span>Agenda</span>
              {calendar?.status === "connected" && (
                <b className="nav-status">●</b>
              )}
            </button>
            <button
              type="button"
              className={`nav-item ${view === "settings" ? "active" : ""}`}
              onClick={() => setView("settings")}
            >
              <AppIcon name="settings" />
              <span>Configurações</span>
            </button>
          </nav>
          <button type="button" className={`nav-item ${view === "plans" ? "active" : ""}`} onClick={()=>setView("plans")}><AppIcon name="overview"/><span>Planos</span></button>
          <div className="sidebar-bottom">
            <span className="presence" />
            <div>
              <strong>Ambiente seguro</strong>
              <small>Sessão da empresa</small>
            </div>
            <button type="button" className="sidebar-signout" onClick={logout}>
              <AppIcon name="logout" />
              <span>Sair</span>
            </button>
          </div>
        </aside>
        {view === "inbox" ? (
          <>
            <section className="inbox-panel">
              <header className="panel-header">
                <div>
                  <p className="eyebrow">ATENDIMENTO</p>
                  <h1>Conversas</h1>
                </div>
                <div className="panel-header-actions">
                  <span className="count-pill">{conversations.length}</span>
                  <OperationalBell token={token} tenantId={tenantId} onOpenConversation={(id) => { openConversation(id); setView("inbox"); setMobileThreadOpen(true); }} />
                </div>
              </header>
              <div className="tenant-switch">
                <span>EMPRESA SELECIONADA</span>
                <div className="select-wrap">
                  <select
                    value={tenantId}
                    onChange={(event) => {
                      setTenantId(event.target.value);
                      setSelectedId("");
                      setMessages([]);
                      setMobileThreadOpen(false);
                    }}
                    aria-label="Selecionar empresa"
                  >
                    {tenants.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="manage-button"
                  onClick={openEdit}
                  disabled={!profile}
                >
                  Editar empresa selecionada
                </button>
              </div>
              {subscription && (
                <section
                  className={"subscription-card " + subscription.status}
                  aria-label="Situação comercial"
                >
                  <div>
                    <p>PLANO E ACESSO</p>
                    <strong>{subscription.planName}</strong>
                    <small>{subscriptionSummary(subscription)}</small>
                    {subscription.usage && (
                      <small className="subscription-usage">
                        {subscription.usage.remaining.toLocaleString("pt-BR")}{" "}
                        de {subscription.usage.limit.toLocaleString("pt-BR")}{" "}
                        respostas de IA disponíveis
                      </small>
                    )}
                    {subscription.pendingPaidPlan && (
                      <small className="subscription-confirmed">
                        {subscription.pendingPaidPlan.planName} confirmado para o próximo período pago.
                      </small>
                    )}
                    {subscription.financial && (
                      <small className="subscription-financial" aria-live="polite">
                        {subscription.financial.paymentMethod === "boleto" ? "Boleto" : subscription.financial.paymentMethod === "credit_card" ? "Cartão" : "Pix"}
                        {" · "}{subscription.financial.status === "received" ? "pagamento recebido" : subscription.financial.status === "confirmed" ? "pagamento confirmado" : subscription.financial.status === "overdue" ? "cobrança vencida" : "aguardando pagamento"}
                        {subscription.financial.dueAt ? ` · vencimento ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(new Date(subscription.financial.dueAt))}` : ""}
                      </small>
                    )}
                  </div>
                  <div className="commercial-action">
                    <span>{subscriptionLabel(subscription)}</span>
                    <button type="button" className="subscription-cta" onClick={() => setView("plans")}>Ver planos e contratação</button>
                  </div>
                </section>
              )}
              <label className="search">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar conversa"
                  aria-label="Buscar conversa"
                />
              </label>
              <div className="inbox-filter">
                <button
                  type="button"
                  className={modeFilter === "all" ? "active" : ""}
                  onClick={() => setModeFilter("all")}
                >
                  Todas
                </button>
                <button
                  type="button"
                  className={modeFilter === "ai" ? "active" : ""}
                  onClick={() => setModeFilter("ai")}
                >
                  IA
                </button>
                <button
                  type="button"
                  className={modeFilter === "human" ? "active" : ""}
                  onClick={() => setModeFilter("human")}
                >
                  Humano
                </button>
              </div>
              <div className="list-heading">
                <span>RECENTES</span>
                <span>{visible.length}</span>
              </div>
              <div className="conversation-list">
                {visible.map((conversation) => (
                  <button
                    type="button"
                    key={conversation.id}
                    onClick={() => {
                      openConversation(conversation.id);
                      setMobileThreadOpen(true);
                    }}
                    className={`conversation ${conversation.id === selectedId ? "selected" : ""} ${conversation.has_unread ? "unread" : ""}`}
                  >
                    <Avatar
                      name={displayName(conversation)}
                      url={conversation.avatar_url}
                    />
                    <span className="conversation-copy">
                      <span className="conversation-title">
                          <strong>{displayName(conversation)}</strong>
                          {conversation.has_unread && <span className="conversation-unread" aria-label="Nova mensagem" role="img" />}
                        <time>
                          {formatTime(
                            conversation.last_at || conversation.updated_at,
                          )}
                        </time>
                      </span>
                      <span className="conversation-preview">
                        {conversation.last_text || "Conversa iniciada"}
                      </span>
                      <span className={`mode ${conversation.ai_mode}`}>
                        {modeLabel(conversation.ai_mode)}
                      </span>
                    </span>
                  </button>
                ))}
                {!visible.length && (
                  <div className="empty-list">
                    <strong>Sem conversas por enquanto</strong>
                    <p>As novas conversas aparecerão aqui.</p>
                  </div>
                )}
              </div>
              <aside className="activation-dock">
                <div>
                  <p>WHATSAPP DA EMPRESA</p>
                  <strong>{tenant?.name ?? "Selecione uma empresa"}</strong>
                  <ChannelOperationalStatus token={token} tenantId={tenantId} compact />
                </div>
                <button
                  className="secondary"
                  onClick={() => void beginPairing()}
                  disabled={
                    busy || !tenantId || channel?.status === "connected"
                  }
                >
                  {channel?.status === "connected"
                    ? "Conectado"
                    : "Conectar WhatsApp"}
                </button>
                {qrImage && (
                  <div className="qr-popover">
                    <img
                      src={qrImage}
                      alt="QR Code para vincular WhatsApp de teste"
                    />
                    <span>
                      Escaneie somente com um número autorizado de teste.
                    </span>
                  </div>
                )}
              </aside>
            </section>
            <section className="thread-panel">
              {selected ? (
                <>
                  <header className="thread-header">
                    <div className="thread-person">
                      <button
                        type="button"
                        className="mobile-back"
                        onClick={() => setMobileThreadOpen(false)}
                      >
                        ←
                      </button>
                      <Avatar
                        large
                        name={displayName(selected)}
                        url={selected.avatar_url}
                      />
                      <div className="thread-person-copy">
                        <h2>{displayName(selected)}</h2>
                        <p>
                          {selected.ai_mode === "human" ? "Atendimento humano" : selected.ai_mode === "paused" ? "Atendimento pausado" : "IA ativa nesta conversa"}
                        </p>
                      </div>
                    </div>
                    <div className="thread-actions">
                      <button
                        className="secondary"
                        type="button"
                        onClick={() => {
                          setCrmFocusId(selected.id);
                          setView("pipeline");
                        }}
                      >
                        Ver no CRM
                      </button>
                      {selected.ai_mode === "human" ? (
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={() => void transition("resume-ai")}
                        >
                          Retomar IA
                        </button>
                      ) : (
                        <button
                          className="primary"
                          disabled={busy}
                          onClick={() => void transition("claim")}
                        >
                          Assumir conversa
                        </button>
                      )}
                    </div>
                  </header>
                  <div className="inbox-commercial-brief">
                    <CommercialBrief
                      insight={commercialInsight}
                      loading={commercialInsightLoading}
                      onRefresh={refreshCommercialInsight}
                      expanded={commercialBriefExpanded}
                      onToggle={toggleCommercialBrief}
                      compact
                    />
                  </div>
                  <div
                    className="messages"
                    ref={messagesPane}
                    onScroll={trackMessageScroll}
                  >
                    {messages.map((message) => (
                      <article
                        key={message.id}
                        className={`message ${message.actor} ${message.delivery_reason === "human_handoff" ? "interrupted" : ""}`}
                      >
                        <div className="message-label">
                          <span>
                            {message.actor === "customer"
                              ? "Cliente"
                              : message.actor === "ai"
                                ? "G3.AI"
                                : "Operador"}
                          </span>
                          <time>{formatTime(message.created_at)}</time>
                        </div>
                        {message.content_type === "audio" ? (
                          <AudioPlayer message={message} />
                        ) : (
                          <p>
                            {message.content_type === "image"
                              ? "Imagem"
                              : message.text
                                ? ""
                                : "Mensagem"}
                            {message.content_type === "image"
                              ? message.text
                                ? ` · ${message.text}`
                                : " recebida"
                              : message.text}
                          </p>
                        )}
                        {message.actor !== "customer" && <DeliveryIndicator message={message} />}
                      </article>
                    ))}
                  </div>
                  <form className="composer" onSubmit={send}>
                    {selected.ai_mode === "human" && (
                      <div className="record-controls">
                        {recording === "idle" && !recorded && (
                          <button
                            type="button"
                            className="record-button"
                            onClick={() => void beginRecording()}
                            disabled={busy}
                            aria-label="Gravar áudio"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z" />
                            </svg>
                            Gravar
                          </button>
                        )}
                        {(recording === "recording" ||
                          recording === "paused") && (
                          <>
                            <div
                              className={`live-recorder ${recording === "paused" ? "paused" : ""}`}
                              role="status"
                            >
                              <span className="recording-dot" />
                              <span className="live-wave" aria-hidden="true">
                                {liveWave.map((value, index) => (
                                  <i
                                    key={index}
                                    style={{ height: `${value}%` }}
                                  />
                                ))}
                              </span>
                              <time>{durationLabel(recordingSeconds)}</time>
                            </div>
                            <button
                              type="button"
                              className="record-button recording"
                              onClick={pauseRecording}
                            >
                              {recording === "paused" ? "Continuar" : "Pausar"}
                            </button>
                            <button
                              type="button"
                              className="record-button"
                              onClick={stopRecording}
                            >
                              Encerrar
                            </button>
                            <button
                              type="button"
                              className="record-cancel"
                              onClick={discardRecording}
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                        {recorded && recording === "ready" && (
                          <div className="record-preview">
                            <AudioPlayer
                              preview
                              message={{
                                actor: "human",
                                media_url: recorded.url,
                                media_duration_seconds:
                                  recorded.durationSeconds,
                                text: "",
                              }}
                            />
                            <button type="button" onClick={discardRecording}>
                              Descartar
                            </button>
                            <button
                              type="button"
                              className="record-send"
                              disabled={busy}
                              onClick={() => void sendRecording()}
                            >
                              Enviar áudio
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <textarea
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      disabled={
                        selected.ai_mode !== "human" ||
                        busy ||
                        recording !== "idle" ||
                        !!recorded
                      }
                      placeholder={
                        selected.ai_mode === "human"
                          ? "Escreva sua mensagem…"
                          : "Assuma a conversa para responder"
                      }
                    />
                    <button
                      className="send"
                      disabled={
                        selected.ai_mode !== "human" ||
                        busy ||
                        !draft.trim() ||
                        recording !== "idle" ||
                        !!recorded
                      }
                    >
                      Enviar
                    </button>
                  </form>
                </>
              ) : (
                <div className="empty-thread">
                  <h2>Seu próximo atendimento começa aqui</h2>
                  <p>Selecione uma conversa para acompanhar o histórico.</p>
                </div>
              )}
            </section>
          </>
        ) : view === "plans" ? (
          <PlansWorkspace tenantId={tenantId} token={token} owner={tenant?.role==='owner'} subscription={subscription} api={api}/>
        ) : view === "calendar" ? (
          <CalendarWorkspace
            token={token}
            tenantId={tenantId}
            tenantName={tenant?.name ?? ""}
            role={tenant?.role}
            request={api}
            onError={setError}
            onOpenInbox={(conversationId) => { openConversation(conversationId); setView("inbox"); setMobileThreadOpen(true); }}
          />
        ) : (
          <CrmWorkspace
            token={token}
            tenantId={tenantId}
            tenantName={tenant?.name ?? ""}
            tenantRole={tenant?.role}
            view={view}
            tenants={tenants}
            onTenantChange={(id) => {
              setTenantId(id);
              setSelectedId("");
              setMessages([]);
            }}
            subscription={subscription}
            channel={channel}
            focusConversationId={crmFocusId}
            onFocusConsumed={() => setCrmFocusId("")}
            onTenantNameChange={(name) =>
              setTenants((current) =>
                current.map((item) =>
                  item.id === tenantId ? { ...item, name } : item,
                ),
              )
            }
            onOpenPreparation={() => setView("preparation")}
            onOpenInbox={(conversationId) => {
              openConversation(conversationId);
              setView("inbox");
              setMobileThreadOpen(true);
            }}
          />
        )}
      </main>
      <nav className="crm-mobile-nav" aria-label="Navegação mobile">
        <button type="button" className={view==='plans'?'active':''} onClick={()=>setView('plans')}><AppIcon name="overview"/><span>Planos</span></button>
        <button
          type="button"
          className={view === "dashboard" ? "active" : ""}
          onClick={() => setView("dashboard")}
        >
          <AppIcon name="overview" />
          <span>Visão geral</span>
        </button>
        <button
          type="button"
          className={view === "inbox" ? "active" : ""}
          onClick={() => setView("inbox")}
        >
          <AppIcon name="inbox" />
          <span>Inbox</span>
        </button>
        <button
          type="button"
          className={view === "pipeline" ? "active" : ""}
          onClick={() => setView("pipeline")}
        >
          <AppIcon name="pipeline" />
          <span>Pipeline</span>
        </button>
        <button
          type="button"
          className={view === "preparation" ? "active" : ""}
          onClick={() => setView("preparation")}
        >
          <AppIcon name="brain" />
          <span>Minha IA</span>
        </button>
        <button type="button" className={view === "calendar" ? "active" : ""} onClick={() => setView("calendar")}>
          <AppIcon name="calendar" />
          <span>Agenda</span>
        </button>
        <button
          type="button"
          className={view === "settings" ? "active" : ""}
          onClick={() => setView("settings")}
        >
          <AppIcon name="settings" />
          <span>Ajustes</span>
        </button>
      </nav>
      {formMode && (
        <div
          className="setup-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Editar empresa"
        >
          <form className="setup-card" onSubmit={saveCompany}>
            <header>
              <div>
                <p className="eyebrow">EMPRESA SELECIONADA</p>
                <h2>{`Editar ${tenant?.name ?? "empresa"}`}</h2>
              </div>
              <button
                type="button"
                className="close"
                onClick={() => setFormMode(null)}
                aria-label="Fechar"
              >
                ×
              </button>
            </header>
            <p className="setup-copy">
              Você está alterando apenas a empresa selecionada. As atualizações
              não afetam outras empresas.
            </p>
            <div className="setup-grid">
              <label>
                Nome da empresa
                <input
                  required
                  value={form.companyName}
                  onChange={(event) =>
                    updateForm({ companyName: event.target.value })
                  }
                />
              </label>
              <label>
                Segmento
                <input
                  required
                  value={form.businessArea}
                  onChange={(event) =>
                    updateForm({ businessArea: event.target.value })
                  }
                />
              </label>
              <label className="full">
                Serviços <small>Separe por vírgula</small>
                <input
                  value={form.services.join(", ")}
                  onChange={(event) =>
                    updateForm({
                      services: event.target.value
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </label>
              <label className="full">
                Informações comerciais
                <textarea
                  value={form.commercialInfo}
                  onChange={(event) =>
                    updateForm({ commercialInfo: event.target.value })
                  }
                />
              </label>
              <label>
                Horários
                <input
                  value={form.businessHours}
                  onChange={(event) =>
                    updateForm({ businessHours: event.target.value })
                  }
                />
              </label>
              <label>
                Números de teste{" "}
                <small>
                  Use (11) 99999-9999; separados por vírgula. Salvamos com DDI
                  55.
                </small>
                <input
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  value={form.allowedContacts}
                  onChange={(event) =>
                    updateForm({ allowedContacts: event.target.value })
                  }
                />
              </label>
              <label className="full">
                Regras de atendimento
                <textarea
                  required
                  value={form.attendanceRules}
                  onChange={(event) =>
                    updateForm({ attendanceRules: event.target.value })
                  }
                />
              </label>
              <label className="full">
                Instruções específicas da IA
                <textarea
                  value={form.aiInstructions}
                  onChange={(event) =>
                    updateForm({ aiInstructions: event.target.value })
                  }
                />
              </label>
            </div>
            {formError && (
              <p className="form-error setup-form-error" role="alert">
                {formError}
              </p>
            )}
            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setFormMode(null)}
              >
                Cancelar
              </button>
              <button className="primary" disabled={busy}>
                Salvar alterações
              </button>
            </footer>
          </form>
        </div>
      )}
      {calendarModal && (
        <div
          className="setup-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Agenda Google"
        >
          <form className="setup-card" onSubmit={saveCalendarSetup}>
            <header>
              <div>
                <p className="eyebrow">AGENDA REAL</p>
                <h2>Google Calendar</h2>
              </div>
              <button
                type="button"
                className="close"
                onClick={() => setCalendarModal(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </header>
            <p className="setup-copy">
              A agenda só confirma um horário depois que o Google Calendar cria
              o evento.
            </p>
            {!calendar?.oauthConfigured ? (
              <p className="form-error setup-form-error">
                A integração OAuth ainda não foi configurada com segurança no
                staging.
              </p>
            ) : calendar.status !== "connected" ? (
              <button
                type="button"
                className="primary"
                disabled={calendarSaving}
                onClick={() => void connectGoogleCalendar()}
              >
                Conectar conta Google
              </button>
            ) : (
              <>
                <div className="setup-grid">
                  <label className="full">
                    Conta conectada
                    <input
                      value={calendar.accountEmail ?? "Conta Google autorizada"}
                      disabled
                    />
                  </label>
                  <label className="full">
                    Calendário
                    <select
                      value={calendar.calendarId ?? ""}
                      onChange={(event) =>
                        void selectGoogleCalendar(event.target.value)
                      }
                      disabled={calendarSaving}
                    >
                      <option value="">Selecione o calendário</option>
                      {googleCalendars.map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name}
                          {item.primary ? " (principal)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Fuso horário
                    <input
                      name="timezone"
                      defaultValue={
                        calendar.settings?.timezone ?? "America/Sao_Paulo"
                      }
                    />
                  </label>
                  <label>
                    <span>Ativar agenda</span>
                    <input
                      name="enabled"
                      type="checkbox"
                      defaultChecked={calendar.settings?.enabled ?? false}
                    />
                  </label>
                  <label>
                    Início (seg–sex)
                    <input
                      name="weekdayStart"
                      type="time"
                      defaultValue={
                        calendar.settings?.businessHours?.[0]?.start ?? "09:00"
                      }
                    />
                  </label>
                  <label>
                    Fim (seg–sex)
                    <input
                      name="weekdayEnd"
                      type="time"
                      defaultValue={
                        calendar.settings?.businessHours?.[0]?.end ?? "18:00"
                      }
                    />
                  </label>
                  <label>
                    Intervalo entre opções
                    <input
                      name="slotIntervalMinutes"
                      type="number"
                      min="5"
                      max="180"
                      defaultValue={
                        calendar.settings?.slotIntervalMinutes ?? 30
                      }
                    />
                  </label>
                  <label>
                    Antecedência mínima (min)
                    <input
                      name="minimumNoticeMinutes"
                      type="number"
                      min="0"
                      max="10080"
                      defaultValue={
                        calendar.settings?.minimumNoticeMinutes ?? 60
                      }
                    />
                  </label>
                  <label>
                    Serviço agendável
                    <input
                      name="serviceName"
                      placeholder="Ex.: Avaliação estética"
                      defaultValue={calendar.services[0]?.name ?? ""}
                    />
                  </label>
                  <label>
                    Duração do serviço (min)
                    <input
                      name="serviceDurationMinutes"
                      type="number"
                      min="5"
                      max="480"
                      defaultValue={
                        calendar.services[0]?.duration_minutes ?? 30
                      }
                    />
                  </label>
                </div>
                <section className="calendar-appointments">
                  <h3>Próximos agendamentos</h3>
                  <p>
                    {calendar?.module?.operational
                      ? "Agenda operacional: disponibilidade e confirmações usam o Google Calendar."
                      : "A agenda só opera quando a G3.AI permite o módulo, o proprietário ativa e o Google está conectado."}
                  </p>
                  {calendarAppointments.slice(0, 6).map((appointment) => (
                    <article key={appointment.id}>
                      <strong>{appointment.service_name ?? "Serviço"}</strong>
                      <span>
                        {new Intl.DateTimeFormat("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        }).format(new Date(appointment.starts_at))}{" "}
                        · {appointment.status}
                      </span>
                    </article>
                  ))}
                  {!calendarAppointments.length && (
                    <small>Nenhum agendamento registrado nesta empresa.</small>
                  )}
                </section>
                <footer>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setCalendarModal(false)}
                  >
                    Fechar
                  </button>
                  <button
                    className="primary"
                    disabled={calendarSaving || !calendar.calendarId}
                  >
                    Salvar agenda
                  </button>
                </footer>
              </>
            )}
          </form>
        </div>
      )}
      {error && (
        <p className="toast global-toast" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
const route = window.location.pathname.replace(/\/+$/, "") || "/";
const bookingToken = /^\/reservar\/([A-Za-z0-9_-]{32,128})$/.exec(route)?.[1];
const recoveryToken = new URLSearchParams(window.location.search).get("token");
createRoot(document.getElementById("root")!).render(
  bookingToken ? (
    <BookingPage token={bookingToken} />
  ) : route === "/recuperar-acesso" ? (
    <PasswordRecovery token={recoveryToken} />
  ) : route === "/admin" ? (
    <AdminApp />
  ) : route === "/app" || route === "/inbox" || route === "/qa/preparation-360-v2" ? (
    <App />
  ) : (
    <MarketingApp route={route} />
  ),
);
